import * as isNumber from 'lodash/isNumber'
import { SimpleGuard, WatchingGuard } from 'interfaces/guards'
import { TimeframeTyped } from 'interfaces/global'
import { ActivePeergroupsTyped } from 'interfaces/peergroups'
import { AccountBreakdownOwnProps, AccountTyped } from 'interfaces/accounts'
import { compose } from 'redux'
import {
  getAccountRatesActionCreator,
  getMemberAccountBreakdownActionCreator,
  getMemberAccountTrendsActionCreator,
  getMemberAllAccountsActionCreator,
  getPeergroupAccountBreakdownActionCreator,
  getPeergroupAccountTrendsActionCreator,
} from 'actions/accountsActions'
import {
  hasPeergroupAccountBreakdownsSelector,
  isPeergroupAccountTrendsLoadedSelector,
  memberAccountBreakdownSelector,
  memberAccountTrendSelector,
} from 'selectors/accountSelectors'
import { selectedPeergroupIdSelector, yourPeergroupIdSelector } from 'selectors/peergroupSelectors'
import { getTimeframeLastMonth } from 'helpers/dateTimeHelpers'
import { AccountTrendOwnProps } from 'components/features/pfm/LineChart/AccountsTrendModuleContainer'
import { createGuardOrderError } from 'helpers/errorHelpers'

// ===========================================================================//
//                                  TRENDS                                    //
// ===========================================================================//

export const memberAccountTrendsGuard: SimpleGuard<AccountTrendOwnProps> = {
  name: 'Member Account Trends',
  checker: (state, ownProps) =>
    Boolean(memberAccountTrendSelector(state, ownProps)),
  loader: (dispatch, { type, timeframe, categoryId }) =>
    dispatch(getMemberAccountTrendsActionCreator(type, timeframe, categoryId)),
}

type PeergroupAccountTrendWatch = AccountTrendOwnProps & ActivePeergroupsTyped
export const peergroupAccountTrendsGuard: WatchingGuard<AccountTrendOwnProps, PeergroupAccountTrendWatch> = {
  name: 'Peer Group Account Trends',
  checker: isPeergroupAccountTrendsLoadedSelector,
  watcher: (state, ownProps) => {
    const peersId = yourPeergroupIdSelector(state)
    const othersId = selectedPeergroupIdSelector(state)

    return {
      ...ownProps,
      peersId,
      othersId,
    }
  },
  loader: (dispatch, { type, categoryId, peersId, othersId }) => {
    if (!isNumber(peersId) || !isNumber(othersId)) {
      throw createGuardOrderError('peergroupAccountTrendsGuard', 'peergroupsGuard')
    }

    dispatch(getPeergroupAccountTrendsActionCreator(type, categoryId, [peersId, othersId]))
  },
}

// ===========================================================================//
//                                 BREAKDOWN                                  //
// ===========================================================================//

export const memberAccountsBreakdownGuard: SimpleGuard<AccountBreakdownOwnProps> = {
  name: 'Member Account Breakdown',
  checker: compose(Boolean, memberAccountBreakdownSelector),
  loader: (dispatch, { type, timeframe }) =>
    dispatch(getMemberAccountBreakdownActionCreator(type, getTimeframeLastMonth(timeframe))),
}

type PeergroupAccountsBreakdownWatch = AccountBreakdownOwnProps & ActivePeergroupsTyped
export const peergroupAccountsBreakdownGuard: WatchingGuard<AccountBreakdownOwnProps, PeergroupAccountsBreakdownWatch> = {
  name: 'Peer Group Account Breakdown',
  checker: hasPeergroupAccountBreakdownsSelector,
  watcher: (state, ownProps) => {
    const peersId = yourPeergroupIdSelector(state)
    const othersId = selectedPeergroupIdSelector(state)

    return {
      ...ownProps,
      peersId,
      othersId,
    }
  },
  loader: (dispatch, { type, timeframe, peersId, othersId }) => {
    if (!isNumber(peersId) || !isNumber(othersId)) {
      throw createGuardOrderError('peergroupAccountsBreakdownGuard', 'peergroupsGuard')
    }

    dispatch(
      getPeergroupAccountBreakdownActionCreator(type, getTimeframeLastMonth(timeframe), [peersId, othersId])
    )
  },
}

// ===========================================================================//
//                                  DETAILS                                   //
// ===========================================================================//

export const memberAllAccountsGuard: SimpleGuard<AccountTyped> = {
  name: 'Account Details',
  checker: (state, { type }) =>
    Boolean(state.data[type].accounts),
  loader: (dispatch, { type }) =>
    dispatch(getMemberAllAccountsActionCreator.request({ type })),
}

// ===========================================================================//
//                                   RATES                                    //
// ===========================================================================//

type YourPeersIdTyped = { peersId: number | null }
export const accountRatesGuard: WatchingGuard<AccountTyped & TimeframeTyped, AccountTyped & TimeframeTyped & YourPeersIdTyped> = {
  name: 'Account Rates',
  checker: (state, { type, timeframe }) => {
    const rates = state.data[type].rates
    return Boolean(rates && rates[timeframe])
  },
  watcher: (state, ownProps) => {
    const peersId = yourPeergroupIdSelector(state)

    return {
      ...ownProps,
      peersId,
    }
  },
  loader: (dispatch, { type, timeframe, peersId }) => {
    if (!isNumber(peersId)) {
      throw createGuardOrderError('accountRatesGuard', 'peergroupsGuard')
    }

    dispatch(getAccountRatesActionCreator(type, timeframe, peersId))
  },
}
